import React, { useState, useEffect } from "react";
import InputText from "../../../../components/inputs/InputText";
import InputSelect from "../../../../components/inputs/InputSelect";
import ModelBtn from "../../../../components/commonModal/ModelBtn";

export interface Plan {
  id: number;
  name: string;
  billingType: "monthly" | "yearly" | "weekly" | "daily" | "custom";
  amount: number;
  currency: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customDays?: number;
}

interface PlanFormProps {
  selectedPlan: Plan | null;
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

const PlanForm: React.FC<PlanFormProps> = ({
  selectedPlan,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    billingType: "monthly",
    amount: "",
    currency: "LKR",
    description: "",
    isActive: true,
    customDays: "",
  });

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (selectedPlan) {
      setFormData({
        name: selectedPlan.name,
        billingType: selectedPlan.billingType,
        amount: selectedPlan.amount.toString(),
        currency: selectedPlan.currency,
        description: selectedPlan.description || "",
        isActive: selectedPlan.isActive,
        customDays: selectedPlan.customDays?.toString() || "",
      });
    } else {
      setFormData({
        name: "",
        billingType: "monthly",
        amount: "",
        currency: "LKR",
        description: "",
        isActive: true,
        customDays: "",
      });
    }
    setErrors({});
  }, [selectedPlan]);

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = "Plan name is required";
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
    }

    if (!formData.billingType) {
      newErrors.billingType = "Billing type is required";
    }

    if (formData.billingType === "custom" && !formData.customDays) {
      newErrors.customDays = "Custom days is required";
    }

    if (
      formData.billingType === "custom" &&
      parseInt(formData.customDays) <= 0
    ) {
      newErrors.customDays = "Custom days must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const planData: any = {
      ...formData,
      amount: parseFloat(formData.amount),
    };

    if (formData.billingType === "custom") {
      planData.customDays = parseInt(formData.customDays);
    }

    onSubmit(planData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: "" }));
    }
  };

  const billingTypeOptions = [
    { value: "daily", name: "Daily" },
    { value: "weekly", name: "Weekly" },
    { value: "monthly", name: "Monthly" },
    { value: "yearly", name: "Yearly" },
    { value: "custom", name: "Custom (Manual Days)" },
  ];

  const currencyOptions = [
    { value: "LKR", name: "LKR - Sri Lankan Rupee" },
    { value: "USD", name: "USD - US Dollar" },
    { value: "EUR", name: "EUR - Euro" },
    { value: "GBP", name: "GBP - British Pound" },
    { value: "AUD", name: "AUD - Australian Dollar" },
    { value: "CAD", name: "CAD - Canadian Dollar" },
    { value: "JPY", name: "JPY - Japanese Yen" },
    { value: "CNY", name: "CNY - Chinese Yuan" },
    { value: "INR", name: "INR - Indian Rupee" },
    { value: "SGD", name: "SGD - Singapore Dollar" },
  ];

  return (
    <div className="add-product-form">
      <InputText
        label="Plan Name"
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        errors={errors.name}
        placeholder="e.g., Basic, Pro, Enterprise"
        required
      />

      <InputSelect
        label="Billing Type"
        value={formData.billingType}
        onChange={(e: any) => handleInputChange("billingType", e.target.value)}
        option={billingTypeOptions}
        errors={errors.billingType}
        required
      />

      {formData.billingType === "custom" && (
        <InputText
          label="Custom Days"
          value={formData.customDays}
          onChange={(e) => handleInputChange("customDays", e.target.value)}
          errors={errors.customDays}
          placeholder="e.g., 15, 45, 90"
          type="number"
          required
        />
      )}

      <InputText
        label="Amount"
        value={formData.amount}
        onChange={(e) => handleInputChange("amount", e.target.value)}
        errors={errors.amount}
        placeholder="0.00"
        type="number"
        required
      />

      <InputSelect
        label="Currency"
        value={formData.currency}
        onChange={(e: any) => handleInputChange("currency", e.target.value)}
        option={currencyOptions}
      />

      <InputText
        label="Description"
        value={formData.description}
        onChange={(e) => handleInputChange("description", e.target.value)}
        placeholder="Plan description..."
      />

      <div className="checkbox-field">
        <label>
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleInputChange("isActive", e.target.checked)}
          />
          Active
        </label>
      </div>

      <ModelBtn
        btnText1={selectedPlan ? "Update Plan" : "Add Plan"}
        handleClose={onCancel}
        handleClick={handleSubmit}
      />
    </div>
  );
};

export default PlanForm;
